import Accordion from '@/components/shared/Accordion/accordion.component';
import Tabs from '@/components/shared/Tabs/tabs.component';
import Modal from '@/components/shared/modal.component';
import Typography from '@/components/shared/typography/typography.component';
import myImageLoader from '@/image.local.loader';
import { ImperfectionReportSectionGrouped, highlightsType } from '@/types';
import Image from 'next/image';
import {
    AlertCircleYellow,
    CheckCircleGreenGradient,
    CloseIcon,
    InfoCircleBlue,
    AlertCircleInactive,
    CheckCircleInactive,
    NotesCircleInactive,
    CheckCircleGreen,
    ChevronDownArroy,
    chevronUpArrowBlack,
    CarFactoryPaint,
} from 'public/Images';
import { FC, Fragment, useState } from 'react';
import AccordionGoodPartsContent from './accordionGoodPartsContent';
import NotesSectionContent from './notesSectionContent';
import ExteriorPartsContent from './exteriorPartsContent';
import { TFunction } from 'i18next';

interface GrouppedImprefectionReportModalProps {
    open: boolean;
    setOpen: () => void;
    carDisplayName: string;
    carImage: string;
    imperfectionDataGroupped: ImperfectionReportSectionGrouped;
    initialActiveTab: number;
    highlights: highlightsType | null;
    t: TFunction<'translation', undefined>;
    IS_RTL: boolean;
}

const GrouppedImprefectionReportModal: FC<
    GrouppedImprefectionReportModalProps
> = ({
    open,
    setOpen,
    carDisplayName,
    carImage,
    imperfectionDataGroupped,
    initialActiveTab,
    highlights,
    IS_RTL,
    t,
}) => {
    const [currentOpenIndex, setCurrentOpenIndex] = useState<number | null>(
        null
    );
    const getCurrentOpenIndexFromAccordion = (currentIndex: number | null) => {
        setCurrentOpenIndex(currentIndex);
    };

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            PapperProps={{
                borderRadius: '12px',
                height: 'calc(100% - 200px)',
            }}
        >
            <div className="w-[680px] min-h-full px-4 bg-[#FFF] bg-gradient-to-b from-[#F3FFFA] via-[#F3FFFA00]/[4%]  flex flex-col relative">
                <div className="flex gap-2 pl-2 py-4 items-center  w-full">
                    <div className="w-[53px] h-10  ">
                        <img
                            className="rounded"
                            src={myImageLoader({
                                src: carImage,
                                width: 200,
                                height: 150,
                                quality: 80,
                            })}
                            alt="car image"
                        />
                    </div>
                    <div className="flex flex-col">
                        <Typography
                            variant="body2SemiBold"
                            className="!text-gray-800"
                        >
                            {t('Inspection Report')}
                        </Typography>
                        <Typography
                            variant="captionMedium"
                            className="!text-gray-600"
                        >
                            {carDisplayName}
                        </Typography>
                    </div>
                    <Image
                        onClick={setOpen}
                        src={CloseIcon}
                        alt="close"
                        className="en:ml-auto ar:mr-auto self-start cursor-pointer"
                    />
                </div>
                <Tabs
                    tabContainerClass="w-full pb-4"
                    initialActiveTab={initialActiveTab}
                    tabContentContainerClass="absolute left-0 top-[60px] w-full h-[calc(100%-70px)] overflow-y-scroll overflow-x-hidden"
                    tabsData={[
                        {
                            title: t('exteriorAndRepaintedTitle'),
                            content: (
                                <>
                                    {!imperfectionDataGroupped.isRepainted && (
                                        <div className="flex flex-col items-center mb-6 py-4">
                                            <Typography
                                                variant="body2SemiBold"
                                                className="!text-gray-700"
                                            >
                                                {t('factoryPaint')}
                                            </Typography>
                                            <img
                                                src={myImageLoader({
                                                    src: CarFactoryPaint.src,
                                                    width: 224,
                                                    height: 100,
                                                    quality: 80,
                                                })}
                                                className="h-24 w-56 ar:scale-x-[-1]"
                                                alt="factoy-paint"
                                            />
                                        </div>
                                    )}
                                    <ExteriorPartsContent
                                        section={
                                            imperfectionDataGroupped.exterior
                                        }
                                        IS_RTL={IS_RTL}
                                    />
                                </>
                            ),
                            activeIcon: imperfectionDataGroupped.isRepainted
                                ? AlertCircleYellow
                                : CheckCircleGreenGradient,
                            inactiveIcon: imperfectionDataGroupped.isRepainted
                                ? AlertCircleInactive
                                : CheckCircleInactive,
                            activeBgColor: imperfectionDataGroupped.isRepainted
                                ? 'bg-warning-25'
                                : 'bg-primary-25',
                            activeBorderColor:
                                imperfectionDataGroupped.isRepainted
                                    ? 'border-warning-600'
                                    : 'border-primary-600',
                            activeTextColor:
                                imperfectionDataGroupped.isRepainted
                                    ? 'text-warning-600'
                                    : 'text-primary-600',
                        },
                        {
                            title: t('goodParts'),
                            content: (
                                <>
                                    <Accordion
                                        getCurrentOpenIndexFromAccordion={
                                            getCurrentOpenIndexFromAccordion
                                        }
                                        items={[
                                            ...imperfectionDataGroupped.unFaulty.map(
                                                (section, index) => {
                                                    return {
                                                        content: (
                                                            <div
                                                                key={`${section.name_en} section-content-good`}
                                                            >
                                                                <div className="w-full h-[1px] mb-4 bg-gray-200 " />
                                                                <div className="flex flex-col gap-3">
                                                                    <AccordionGoodPartsContent
                                                                        arr={
                                                                            section.items
                                                                        }
                                                                        IS_RTL={
                                                                            IS_RTL
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>
                                                        ),
                                                        accordionTabLayout: (
                                                            <Fragment
                                                                key={`${section.name_en} section-layout-good`}
                                                            >
                                                                <div className="en:mr-2.5 ar:ml-2.5 w-6 h-6 self-start">
                                                                    {section.section_good_icon && (
                                                                        <img
                                                                            className="object-fill h-full w-full "
                                                                            src={
                                                                                section.section_good_icon
                                                                            }
                                                                            alt="good section"
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="flex flex-col gap-1 w-[calc(100%-90px)]">
                                                                    <Typography
                                                                        variant="body2SemiBold"
                                                                        className="!text-gray-700 en:hidden ar:mt-0.5"
                                                                    >
                                                                        {
                                                                            section.name_ar
                                                                        }
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2SemiBold"
                                                                        className="!text-gray-700 ar:hidden"
                                                                    >
                                                                        {
                                                                            section.name_en
                                                                        }
                                                                    </Typography>

                                                                    {section.subtitle_en && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            className="!text-gray-500 ar:hidden"
                                                                        >
                                                                            {
                                                                                section.subtitle_en
                                                                            }
                                                                        </Typography>
                                                                    )}
                                                                    {section.subtitle_ar && (
                                                                        <Typography
                                                                            variant="caption"
                                                                            className="!text-gray-500 en:hidden"
                                                                        >
                                                                            {
                                                                                section.subtitle_ar
                                                                            }
                                                                        </Typography>
                                                                    )}
                                                                </div>

                                                                <div
                                                                    className={`flex gap-2 en:ml-auto ar:mr-auto ${
                                                                        section.subtitle_en
                                                                            ? ' self-start'
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    <Image
                                                                        src={
                                                                            CheckCircleGreen
                                                                        }
                                                                        alt="green-tick"
                                                                    />
                                                                    <div className="w-5 h-5">
                                                                        <Image
                                                                            src={
                                                                                currentOpenIndex ===
                                                                                index
                                                                                    ? chevronUpArrowBlack
                                                                                    : ChevronDownArroy
                                                                            }
                                                                            alt="green-tick"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </Fragment>
                                                        ),
                                                    };
                                                }
                                            ),
                                        ]}
                                    />
                                </>
                            ),
                            activeIcon: CheckCircleGreenGradient,
                            inactiveIcon: CheckCircleInactive,
                            activeBgColor: 'bg-primary-25',
                            activeBorderColor: 'border-primary-600',
                            activeTextColor: 'text-primary-600',
                        },
                        {
                            title: t('notesParts'),
                            content: (
                                <NotesSectionContent
                                    highlights={highlights}
                                    imperfectionDataGroupped={
                                        imperfectionDataGroupped
                                    }
                                    IS_RTL={IS_RTL}
                                    t={t}
                                />
                            ),
                            activeIcon: InfoCircleBlue,
                            inactiveIcon: NotesCircleInactive,
                            activeBgColor: 'bg-blue-25',
                            activeBorderColor: 'border-blue-600',
                            activeTextColor: 'text-blue-600',
                        },
                    ]}
                    inactiveTextColor="text-gray-600"
                />
            </div>
        </Modal>
    );
};

export default GrouppedImprefectionReportModal;
